import { QRCode } from "@pandas/components";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import Popup from "@/components/common/Popups/Popup";
import QRCodeHelper from "@/components/device-pairing/QrCodeHelper";
import LoadingDots from "@/components/LoadingDots";
import { servicesOrchestratorValues } from "@/config/services";
import useInfoHelper from "@/hooks/useInfoHelper";
import useInitializationParams from "@/hooks/useInitializationParams";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import useUrlShortener from "@/hooks/useUrlShortener";
import type { PeerMessage } from "@/lib/ably/vision";
import { createVisionDirectHistoryFetcher, useLastMessageAction, useVisionChannel } from "@/lib/ably/vision";
import { useStore } from "@/store";
import { getRedirectToDeviceSearch } from "@/utils/initializationHelpers";
import { getPeerUrl } from "@/utils/urlHelpers";

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/device-pairing")({
	component: DevicePairing,
});

function DevicePairing() {
	const { t, i18n } = useTranslation();
	const sessionId = useStore((state) => state.sessionId);
	const [showModal, setShowModal] = useState(false);
	const [showHelper, setShowHelper] = useState(false);
	const { shortUrl, getShortUrl } = useUrlShortener();
	const { goToNextStep } = useNavigationHelpers();
	const { partnerToken, partnerCode, locationId } = useInitializationParams();
	const redirectToDeviceSearch = getRedirectToDeviceSearch({ sessionId, partnerToken, partnerCode, locationId });
	const hasNavigated = useRef(false);

	const url = getPeerUrl(
		i18n.language,
		partnerCode ?? null,
		partnerToken,
		locationId,
		sessionId ?? null,
		redirectToDeviceSearch,
	);
	const handleAblyMessage = useCallback(
		(message: PeerMessage) => {
			if (message.name === "session-status-update" && message.data.status === "pairing-started") {
				if (hasNavigated.current) return;
				hasNavigated.current = true;
				// Realtime-driven advance (never a user double-tap), and useLastMessage
				// can replay this status on mount — bypass the double-tap cooldown so it
				// is never dropped for arriving within the cooldown window.
				goToNextStep(undefined, { immediate: true });
			}
		},
		[goToNextStep],
	);

	const { channel } = useVisionChannel(sessionId, handleAblyMessage);
	const directFetcher = useMemo(
		() => createVisionDirectHistoryFetcher(servicesOrchestratorValues.credentials.ablyKey, sessionId),
		[sessionId],
	);
	useLastMessageAction(channel, handleAblyMessage, { directFetcher });

	useInfoHelper(() => setShowHelper(true));

	return (
		<>
			<div className="flex flex-col items-center text-center justify-between h-full relative">
				<div className="flex flex-col justify-between items-center h-2/3">
					<h3 className="pt-10 sm:pt-20">{t("instructions.Scan to start the evaluation process.")}</h3>
					<div className="text-center max-w-2xl">
						{sessionId && (
							<QRCode
								id="qr-code"
								size={250}
								className="bg-white rounded-lg p-4 sm:p-8 mx-auto w-[180px] h-[180px] sm:w-[300px] sm:h-[300px]"
								value={url}
							/>
						)}
					</div>
				</div>
				<Button
					intent="tertiary"
					onClick={() => {
						getShortUrl(url);
						setShowModal(true);
					}}
					label={t("btn.Can’t scan QR code?")}
				/>
			</div>
			<Popup
				show={showModal}
				setShow={setShowModal}
				content={
					<div className="flex flex-col justify-center items-center h-full">
						<h4 className="my-6">{t("info pop ups.Visit")}</h4>
						<div className="bg-lightgrey p-4 rounded-lg">{shortUrl ? shortUrl : <LoadingDots />}</div>
					</div>
				}
			/>
			<QRCodeHelper
				showModal={showHelper}
				setShowModal={setShowHelper}
			/>
		</>
	);
}
